/**
 * DangerZoneSection - Account deletion with progressive disclosure
 * Design: Collapsible section with confirmation flow
 */
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteAccount, setAuthError } from '../../../slices/authSlice.mjs';

export default function DangerZoneSection({ disabled }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const inputRef = useRef(null);

    const dispatch = useDispatch();
    const { error, loading } = useSelector((state) => state.auth || {});

    // Focus input when confirmation is shown
    useEffect(() => {
        if (showConfirm && inputRef.current) {
            inputRef.current.focus();
        }
    }, [showConfirm]);

    const handleToggleExpand = () => {
        setIsExpanded(!isExpanded);
        if (isExpanded) {
            // Reset state when collapsing
            setShowConfirm(false);
            setConfirmText('');
            dispatch(setAuthError(''));
        }
    };

    const handleDeleteClick = () => {
        setShowConfirm(true);
    };

    const handleCancel = () => {
        setShowConfirm(false);
        setConfirmText('');
        dispatch(setAuthError(''));
    };

    const handleConfirmDelete = async () => {
        if (confirmText !== 'DELETE') {
            dispatch(setAuthError('Please type DELETE to confirm'));
            return;
        }

        try {
            setIsDeleting(true);
            await dispatch(deleteAccount()).unwrap();
            // Redirect to account deleted page
            if (typeof window !== 'undefined') {
                window.location.href = '/account-deleted';
            }
        } catch (err) {
            setIsDeleting(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleConfirmDelete();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    return (
        <section
            id="danger"
            className={`settings-section danger-zone-section ${isExpanded ? '' : 'collapsed'}`}
        >
            <button
                className={`settings-section-header danger-zone-collapsed ${isExpanded ? 'expanded' : ''}`}
                onClick={handleToggleExpand}
                aria-expanded={isExpanded}
                style={{ cursor: 'pointer', width: '100%', background: 'none', border: 'none', textAlign: 'left' }}
            >
                <span className="settings-section-icon">⚠️</span>
                <h2 className="settings-section-title">Danger Zone</h2>
                <span className="danger-zone-expand-icon">
                    {isExpanded ? '▲' : '▼'}
                </span>
            </button>

            <div className={`danger-zone-content ${isExpanded ? 'expanded' : ''}`}>
                {!showConfirm ? (
                    <>
                        <div className="danger-zone-warning">
                            <span className="danger-zone-warning-icon">🚨</span>
                            <div className="danger-zone-warning-text">
                                <strong>Permanently delete your account</strong>
                                <p style={{ margin: '8px 0 0 0', opacity: 0.8 }}>
                                    Once you delete your account, there is no going back.
                                    All your data, preferences, and history will be permanently removed.
                                </p>
                            </div>
                        </div>
                        <div className="danger-zone-buttons">
                            <button
                                className="danger-zone-btn delete"
                                onClick={handleDeleteClick}
                                disabled={disabled}
                            >
                                Delete My Account
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="danger-zone-warning">
                            <span className="danger-zone-warning-icon">⚠️</span>
                            <div className="danger-zone-warning-text">
                                <strong>Are you sure?</strong>
                                <p style={{ margin: '8px 0 0 0' }}>
                                    Type <strong>DELETE</strong> below to confirm account deletion.
                                </p>
                            </div>
                        </div>

                        <input
                            ref={inputRef}
                            type="text"
                            className={`danger-zone-confirm-input ${error ? 'error' : ''}`}
                            placeholder="Type DELETE to confirm"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                            onKeyDown={handleKeyDown}
                            disabled={loading || isDeleting}
                        />

                        {error && (
                            <p style={{ color: 'var(--theme-error)', fontSize: '14px', marginBottom: '16px' }}>
                                {error}
                            </p>
                        )}

                        <div className="danger-zone-buttons">
                            <button
                                className="danger-zone-btn cancel"
                                onClick={handleCancel}
                                disabled={loading || isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="danger-zone-btn delete"
                                onClick={handleConfirmDelete}
                                disabled={loading || isDeleting || confirmText !== 'DELETE'}
                            >
                                {loading || isDeleting ? 'Deleting...' : 'Confirm Delete'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
